import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import * as XLSX from 'xlsx';
import { ArrowLeft, Plus, Loader2, Search, Download, X, Paperclip, Pencil, AlertTriangle , Trash2} from 'lucide-react';
import { apiClient, extractErrorMessage, getStoredToken } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ExcelToolbar } from '@/components/excel/ExcelToolbar';
import { DriverSelect, toDriverOption } from '@/components/common/DriverSelect';

// PR-D2 — list of dumper loads (scoped to the Dumpers box via X-Project-Id).

interface Trip {
  id: string;
  code: string;
  trip_date: string;
  client_id: string | null;
  client_name: string | null;
  vehicle_id: string | null;
  vehicle_plate: string | null;
  driver_id: string | null;
  driver_name: string | null;
  load_type: string | null;
  ticket_number: string | null;
  km: string | null;
  notes: string | null;
  net_weight_kg: string | null;
  net_weight_tons: string | null;
  rate_per_ton_sar: string | null;
  revenue_sar: string | null;
  status: string;
  invoice_id: string | null;
  scale_ticket_photo_url: string | null;
  doc_tasreeh_url: string | null;
  doc_hopper_url: string | null;
  doc_wasika_url: string | null;
  doc_unload_url: string | null;
  weight_at_load_kg: string | null;
  empty_weight_at_load_kg: string | null;
  empty_weight_at_unload_kg: string | null;
  empty_weight_diff_kg: string | null;
  invoice_code: string | null;
  zoho_invoice_number: string | null;
  weight_at_unload_kg: string | null;
  weight_diff_kg: string | null;
  trip_started_at: string | null;
  trip_ended_at: string | null;
  trip_duration_minutes: string | null;
  trip_start_location: string | null;
  trip_end_location: string | null;
}

interface Pickers {
  vehicles: { id: string; plate: string; code: string }[];
  drivers: { id: string; name: string; code: string ; iqama?: string | null }[];
  clients: { id: string; name: string; contractId: string | null; rate: string | null }[];
}

/** The fields the edit form owns. Times, places, weighbridge readings and the
 *  papers stay in the papers panel, so the two never fight over a value. */
interface EditDraft {
  tripDate: string;
  clientId: string;
  vehicleId: string;
  driverId: string;
  loadType: string;
  ticketNumber: string;
  netWeightKg: string;
  ratePerTonSar: string;
  km: string;
  status: string;
  notes: string;
  // The trip itself. These were only reachable from the side panel, so editing
  // a load meant two dialogs on one record.
  startAt: string;
  endAt: string;
  startPlace: string;
  endPlace: string;
  // The server refuses to confirm a load without both, so the dialog that sets
  // 'confirmed' has to be able to supply them.
  weightAtLoadKg: string;
  weightAtUnloadKg: string;
}

const num = (v: string | null) => (v != null ? Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }) : '—');

const pad2 = (n: number) => String(n).padStart(2, '0');

/** An instant from the API as local "HH:MM", with the date only when it is not
 *  the same day as the load itself - an overnight trip must be obvious. */
function clock(ts: string | null, sameDayAs: string | null): string {
  if (!ts) return '—';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '—';
  const hhmm = `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  const day = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  return sameDayAs && day === sameDayAs.slice(0, 10) ? hhmm : `${day.slice(5)} ${hhmm}`;
}

/**
 * The two halves of a "YYYY-MM-DDTHH:mm" local value.
 *
 * A datetime-local input gives nothing at all until BOTH are filled, which
 * silently drops a date somebody typed. Splitting them lets the date stand on
 * its own and the time be optional.
 */
const splitLocal = (v: string): { date: string; time: string } => {
  const [d = '', t = ''] = (v || '').split('T');
  return { date: d, time: t.slice(0, 5) };
};

/** Put them back together. A date with no time means midnight, not nothing. */
const joinLocal = (date: string, time: string): string =>
  (date ? `${date}T${time || '00:00'}` : '');

/** "210" -> "3h 30m". */
function hoursMins(minutes: string | number | null): string {
  if (minutes == null) return '—';
  const m = Math.round(Number(minutes));
  if (!Number.isFinite(m)) return '—';
  const sign = m < 0 ? '-' : '';
  const a = Math.abs(m);
  return `${sign}${Math.floor(a / 60)}h ${pad2(a % 60)}m`;
}

/** An instant from the API in the shape a datetime-local input expects. */
function toLocalInput(ts: string | null): string {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** datetime-local text -> full ISO instant, or null when blank. */
function fromLocalInput(local: string): string | null {
  if (!local.trim()) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

const nowLocalInput = () => toLocalInput(new Date().toISOString());

const API_BASE = (import.meta.env.VITE_API_URL as string) || 'https://api.jasrhub.com';

// The four papers that travel with a load, in the order the yard collects them.
// `field` must match the column the API returns; `slug` the URL segment.
// The slug and column stay 'hopper' - only the wording shown to people changed
// to "Load paper", so no migration was needed.
const DOC_SLOTS = [
  { slug: 'tasreeh', short: 'T', label: 'Tasreeh',      hint: 'Permit',                 field: 'doc_tasreeh_url' },
  { slug: 'hopper',  short: 'L', label: 'Load paper',   hint: 'Signed at loading',      field: 'doc_hopper_url' },
  { slug: 'wasika',  short: 'W', label: 'Wasika',       hint: 'Consignment note',       field: 'doc_wasika_url' },
  { slug: 'unload',  short: 'U', label: 'Unload paper', hint: 'Signed at the dump site', field: 'doc_unload_url' },
] as const;

type RangeKey = 'today' | 'week' | 'month' | 'all' | 'custom';

const iso = (d: Date) => d.toISOString().slice(0, 10);

/** Inclusive [from, to] for a preset, or null for "all time". */
function presetRange(key: RangeKey): { from: string; to: string } | null {
  const now = new Date();
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  if (key === 'today') return { from: iso(today), to: iso(today) };
  if (key === 'week') {
    // Week starts Saturday, as the Saudi working week does.
    const dow = today.getUTCDay();               // 0 Sun … 6 Sat
    const back = (dow + 1) % 7;                  // Sat -> 0, Sun -> 1, …
    const start = new Date(today); start.setUTCDate(today.getUTCDate() - back);
    const end = new Date(start); end.setUTCDate(start.getUTCDate() + 6);
    return { from: iso(start), to: iso(end) };
  }
  if (key === 'month') {
    const start = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
    const end = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 1, 0));
    return { from: iso(start), to: iso(end) };
  }
  return null;
}

const selectCls = 'h-9 rounded-md border border-input bg-background px-2 text-sm';

export default function DumperTripsPage() {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['dumper-trips'],
    queryFn: async () => (await apiClient.get<{ items: Trip[] }>('/api/v1/dumper-trips')).data.items,
  });
  const all = useMemo(() => data ?? [], [data]);

  // Places used on previous loads, offered as suggestions in the panel.
  const { data: places, refetch: refetchPlaces } = useQuery({
    queryKey: ['dumper-locations'],
    queryFn: async () =>
      (await apiClient.get<{ items: { name: string }[] }>('/api/v1/dumper-trips/locations')).data.items,
  });

  // Dropdown contents for the edit form. Fetched once and reused for every row.
  const { data: pickers } = useQuery({
    queryKey: ['dumper-pickers'],
    queryFn: async () => (await apiClient.get<Pickers>('/api/v1/dumper-trips/pickers')).data,
  });

  // ---- filters
  const [search, setSearch] = useState('');
  const [range, setRange] = useState<RangeKey>('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [vehicle, setVehicle] = useState('all');
  const [driver, setDriver] = useState('all');
  const [client, setClient] = useState('all');
  const [status, setStatus] = useState('all');

  // Dropdown options come from the data itself, so they can never offer a
  // value that would return nothing.
  const options = useMemo(() => {
    const uniq = (xs: (string | null)[]) => [...new Set(xs.filter((x): x is string => !!x))].sort();
    return {
      vehicles: uniq(all.map((r) => r.vehicle_plate)),
      drivers: uniq(all.map((r) => r.driver_name)),
      clients: uniq(all.map((r) => r.client_name)),
      statuses: uniq(all.map((r) => r.status)),
    };
  }, [all]);

  const rows = useMemo(() => {
    const bounds = range === 'custom'
      ? (customFrom || customTo ? { from: customFrom || '0000-01-01', to: customTo || '9999-12-31' } : null)
      : presetRange(range);
    const q = search.trim().toLowerCase();

    return all.filter((r) => {
      const d = (r.trip_date ?? '').slice(0, 10);
      if (bounds && (d < bounds.from || d > bounds.to)) return false;
      if (vehicle !== 'all' && r.vehicle_plate !== vehicle) return false;
      if (driver !== 'all' && r.driver_name !== driver) return false;
      if (client !== 'all' && r.client_name !== client) return false;
      if (status !== 'all' && r.status !== status) return false;
      if (!q) return true;
      // Search every column the operator can see, numbers included.
      return [
        r.code, d, r.client_name, r.vehicle_plate, r.driver_name, r.load_type,
        r.net_weight_tons, r.rate_per_ton_sar, r.revenue_sar, r.status,
        r.weight_at_load_kg, r.weight_at_unload_kg, r.weight_diff_kg,
        r.empty_weight_at_load_kg, r.empty_weight_at_unload_kg, r.empty_weight_diff_kg,
        // The times are searched as they are shown, so typing "07:" finds them.
        clock(r.trip_started_at, r.trip_date), clock(r.trip_ended_at, r.trip_date),
        hoursMins(r.trip_duration_minutes),
        r.trip_start_location, r.trip_end_location,
      ].some((v) => String(v ?? '').toLowerCase().includes(q));
    });
  }, [all, search, range, customFrom, customTo, vehicle, driver, client, status]);

  const totals = rows.reduce(
    (a, r) => ({ tons: a.tons + Number(r.net_weight_tons ?? 0), sar: a.sar + Number(r.revenue_sar ?? 0) }),
    { tons: 0, sar: 0 },
  );

  const filtersActive = search.trim() !== '' || range !== 'all'
    || vehicle !== 'all' || driver !== 'all' || client !== 'all' || status !== 'all';

  function clearFilters() {
    setSearch(''); setRange('all'); setCustomFrom(''); setCustomTo('');
    setVehicle('all'); setDriver('all'); setClient('all'); setStatus('all');
  }

  // ---- selection. Kept as ids so it survives filtering and refetches.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [delOpen, setDelOpen] = useState(false);
  const [delReason, setDelReason] = useState('');
  const [delBusy, setDelBusy] = useState(false);
  const [delResult, setDelResult] = useState<string | null>(null);
  const { user: authUser } = useAuth();
  const canDelete = authUser?.role === 'super_admin' || authUser?.role === 'admin';

  const doDelete = async () => {
    setDelBusy(true);
    try {
      const { data } = await apiClient.post<{ deleted: number; blocked: string[]; message: string }>(
        '/api/v1/dumper-trips/bulk-delete',
        { ids: [...selected], reason: delReason.trim() },
      );
      // The server names what it would not touch. Saying "3 deleted, 1 left on
      // an invoice" beats a silent partial success every time.
      setDelResult(data.message);
      setSelected(new Set());
      await refetch();
    } catch (e) {
      setDelResult(extractErrorMessage(e));
    } finally {
      setDelBusy(false);
    }
  };
  const visibleIds = rows.map((r) => r.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const someVisibleSelected = visibleIds.some((id) => selected.has(id));

  function toggleRow(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function toggleAllVisible() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  }

  /** Export the chosen rows (or everything on screen) to .xlsx, built locally. */
  function exportRows(which: 'selected' | 'filtered') {
    const source = which === 'selected' ? rows.filter((r) => selected.has(r.id)) : rows;
    if (source.length === 0) return;
    const aoa: (string | number)[][] = [[
      'Code', 'Date',
      'Load time', 'Loaded at (place)', 'Unload time', 'Unloaded at (place)', 'Duration minutes',
      'Client', 'Truck', 'Driver', 'Load', 'Tons',
      'Weight at load kg', 'Weight at unload kg', 'Difference kg',
      'Empty at load kg', 'Empty at unload kg', 'Empty difference kg',
      'Rate', 'Revenue', 'Status', 'Invoice',
      ...DOC_SLOTS.map((s) => s.label),
    ]];
    for (const r of source) {
      aoa.push([
        r.code,
        (r.trip_date ?? '').slice(0, 10),
        // Local wall-clock, so the sheet reads the way the yard saw it.
        r.trip_started_at ? toLocalInput(r.trip_started_at).replace('T', ' ') : '',
        r.trip_start_location ?? '',
        r.trip_ended_at ? toLocalInput(r.trip_ended_at).replace('T', ' ') : '',
        r.trip_end_location ?? '',
        r.trip_duration_minutes != null ? Number(r.trip_duration_minutes) : '',
        r.client_name ?? '',
        r.vehicle_plate ?? '',
        r.driver_name ?? '',
        r.load_type ?? '',
        r.net_weight_tons != null ? Number(r.net_weight_tons) : '',
        r.weight_at_load_kg != null ? Number(r.weight_at_load_kg) : '',
        r.weight_at_unload_kg != null ? Number(r.weight_at_unload_kg) : '',
        r.weight_diff_kg != null ? Number(r.weight_diff_kg) : '',
        r.empty_weight_at_load_kg != null ? Number(r.empty_weight_at_load_kg) : '',
        r.empty_weight_at_unload_kg != null ? Number(r.empty_weight_at_unload_kg) : '',
        r.empty_weight_diff_kg != null ? Number(r.empty_weight_diff_kg) : '',
        r.rate_per_ton_sar != null ? Number(r.rate_per_ton_sar) : '',
        r.revenue_sar != null ? Number(r.revenue_sar) : '',
        r.status + (r.invoice_id ? ' · invoiced' : ''),
        r.zoho_invoice_number ?? r.invoice_code ?? '',
        ...DOC_SLOTS.map((s) => (r[s.field] ? 'yes' : '')),
      ]);
    }
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = aoa[0].map((h) => ({ wch: Math.max(String(h).length + 2, 12) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Loads');
    XLSX.writeFile(wb, `dumper-loads-${which}-${iso(new Date())}.xlsx`);
  }

  // ---- documents
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [target, setTarget] = useState<{ tripId: string; slug: string } | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [docError, setDocError] = useState<string | null>(null);

  // The papers panel opens per load. Keeping only the id here means the panel
  // always renders the freshest row after a refetch.
  const [openId, setOpenId] = useState<string | null>(null);
  const openRow = useMemo(() => rows.find((r) => r.id === openId) ?? null, [rows, openId]);

  // Manual weight entry inside the panel: the billed net weight, plus the
  // weighbridge readings taken at loading and at unloading.
  const [weightKg, setWeightKg] = useState('');
  const [loadKg, setLoadKg] = useState('');
  const [unloadKg, setUnloadKg] = useState('');
  const [weightBusy, setWeightBusy] = useState(false);
  const [weightMsg, setWeightMsg] = useState<string | null>(null);

  // Trip start / end, editable from the same panel. datetime-local carries no
  // timezone, so it is read as the browser's local time and sent as an instant.
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [startPlace, setStartPlace] = useState('');
  const [endPlace, setEndPlace] = useState('');
  const [timeBusy, setTimeBusy] = useState(false);
  const [timeMsg, setTimeMsg] = useState<string | null>(null);

  // The empty readings, edited beside their paper exactly as the loaded ones are.
  const [emptyLoadKg, setEmptyLoadKg] = useState('');
  const [emptyUnloadKg, setEmptyUnloadKg] = useState('');

  function openPapers(r: Trip) {
    setOpenId(r.id);
    setWeightKg(r.net_weight_kg ?? '');
    setLoadKg(r.weight_at_load_kg ?? '');
    setEmptyLoadKg(r.empty_weight_at_load_kg ?? '');
    setEmptyUnloadKg(r.empty_weight_at_unload_kg ?? '');
    setUnloadKg(r.weight_at_unload_kg ?? '');
    setStartAt(toLocalInput(r.trip_started_at));
    setEndAt(toLocalInput(r.trip_ended_at));
    setStartPlace(r.trip_start_location ?? '');
    setEndPlace(r.trip_end_location ?? '');
    setWeightMsg(null);
    setTimeMsg(null);
    setDocError(null);
  }

  // Live duration from whatever is in the two boxes, before saving.
  const liveDuration = useMemo(() => {
    const s = fromLocalInput(startAt); const e = fromLocalInput(endAt);
    if (!s || !e) return null;
    return Math.round((new Date(e).getTime() - new Date(s).getTime()) / 60000);
  }, [startAt, endAt]);

  async function saveTimes() {
    if (!openRow) return;
    if (liveDuration != null && liveDuration < 0) {
      setTimeMsg('The trip cannot end before it starts.');
      return;
    }
    setTimeBusy(true); setTimeMsg(null);
    try {
      await apiClient.patch(`/api/v1/dumper-trips/${openRow.id}`, {
        tripStartedAt: fromLocalInput(startAt),
        tripEndedAt: fromLocalInput(endAt),
        tripStartLocation: startPlace.trim() || null,
        tripEndLocation: endPlace.trim() || null,
      });
      await refetch();
      await refetchPlaces();
      setTimeMsg('Saved.');
    } catch (e) {
      setTimeMsg(extractErrorMessage(e));
    } finally {
      setTimeBusy(false);
    }
  }

  // ---- edit one load. Separate from the papers panel so each dialog stays
  // about one thing: this one is the record, that one is what came back from
  // the yard.
  const [editId, setEditId] = useState<string | null>(null);
  const editRow = useMemo(() => all.find((r) => r.id === editId) ?? null, [all, editId]);
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [editBusy, setEditBusy] = useState(false);
  const [editMsg, setEditMsg] = useState<string | null>(null);

  function openEdit(r: Trip) {
    setEditId(r.id);
    setEditMsg(null);
    setDraft({
      tripDate: (r.trip_date ?? '').slice(0, 10),
      clientId: r.client_id ?? '',
      vehicleId: r.vehicle_id ?? '',
      driverId: r.driver_id ?? '',
      loadType: r.load_type ?? '',
      ticketNumber: r.ticket_number ?? '',
      netWeightKg: r.net_weight_kg ?? '',
      ratePerTonSar: r.rate_per_ton_sar ?? '',
      km: r.km ?? '',
      status: r.status,
      notes: r.notes ?? '',
      startAt: toLocalInput(r.trip_started_at),
      endAt: toLocalInput(r.trip_ended_at),
      startPlace: r.trip_start_location ?? '',
      endPlace: r.trip_end_location ?? '',
      weightAtLoadKg: r.weight_at_load_kg ?? '',
      weightAtUnloadKg: r.weight_at_unload_kg ?? '',
    });
  }

  const setField = (k: keyof EditDraft, v: string) => {
    setDraft((d) => (d ? { ...d, [k]: v } : d));
    setEditMsg(null);
  };

  /** Blank -> null, otherwise the number. Returns undefined when unparseable. */
  function numOrNull(s: string): number | null | undefined {
    const t = s.trim();
    if (t === '') return null;
    const n = Number(t);
    return Number.isFinite(n) && n >= 0 ? n : undefined;
  }

  // Live preview, so the effect on revenue is visible before saving.
  const draftRevenue = (() => {
    if (!draft) return null;
    const kg = numOrNull(draft.netWeightKg);
    const rate = numOrNull(draft.ratePerTonSar);
    // == null covers both the blank case and the unparseable one.
    if (kg == null || rate == null) return null;
    return (kg / 1000) * rate;
  })();

  async function saveEdit() {
    if (!editRow || !draft) return;
    if (!draft.tripDate) { setEditMsg('A load needs a date.'); return; }
    // No client is a legitimate state: an imported load arrives without one and
    // the client is set before invoicing, not before saving a weight. Refusing
    // to save would make every other field on the row uneditable until somebody
    // guessed at a client.
    // (client can be filled in later)
    if (!draft.vehicleId) { setEditMsg('Pick a truck.'); return; }

    const kg = numOrNull(draft.netWeightKg);
    const rate = numOrNull(draft.ratePerTonSar);
    const km = numOrNull(draft.km);
    if (kg === undefined) { setEditMsg('Net weight must be a positive number.'); return; }
    if (kg != null && kg > 200_000) { setEditMsg('That is over 200 tonnes - check the number.'); return; }
    if (rate === undefined) { setEditMsg('Rate must be a positive number.'); return; }
    if (km === undefined) { setEditMsg('Km must be a positive number.'); return; }

    // The same guard the side panel applies. Without it a load can be saved
    // ending before it started, and every duration computed from it is negative.
    if (draft.startAt && draft.endAt && draft.endAt < draft.startAt) {
      setEditMsg('The trip cannot end before it starts.'); return;
    }
    const wLoad = numOrNull(draft.weightAtLoadKg);
    const wUnload = numOrNull(draft.weightAtUnloadKg);
    if (wLoad === undefined) { setEditMsg('Weight at load must be a positive number.'); return; }
    if (wUnload === undefined) { setEditMsg('Weight at unload must be a positive number.'); return; }

    setEditBusy(true); setEditMsg(null);
    try {
      await apiClient.patch(`/api/v1/dumper-trips/${editRow.id}`, {
        tripDate: draft.tripDate,
        // Sent only when chosen. The schema takes a uuid or nothing - never
        // null - so an empty box means "leave the client as it is" rather than
        // a rejected save. Choosing one re-points contract_id at that client's
        // active contract, so the rate cannot keep pointing at the last deal.
        ...(draft.clientId ? { clientId: draft.clientId } : {}),
        vehicleId: draft.vehicleId,
        driverId: draft.driverId || null,
        loadType: draft.loadType.trim() || null,
        ticketNumber: draft.ticketNumber.trim() || null,
        netWeightKg: kg,
        ...(rate != null ? { ratePerTonSar: rate } : {}),
        km,
        status: draft.status,
        notes: draft.notes.trim() || null,
        // Through the same converters the panel used, not a second pair: a
        // datetime-local gives wall-clock text with no zone and the column is
        // timestamptz, so two conversions would eventually disagree by three
        // hours and only on some rows.
        tripStartedAt: fromLocalInput(draft.startAt),
        tripEndedAt: fromLocalInput(draft.endAt),
        tripStartLocation: draft.startPlace.trim() || null,
        tripEndLocation: draft.endPlace.trim() || null,
        weightAtLoadKg: wLoad,
        weightAtUnloadKg: wUnload,
      });
      await refetch();
      setEditMsg('Saved.');
    } catch (e) {
      setEditMsg(extractErrorMessage(e));
    } finally {
      setEditBusy(false);
    }
  }

  /** null when blank, NaN when unparseable, else the number. */
  function parseKg(s: string): number | null | typeof NaN {
    const t = s.trim();
    if (t === '') return null;
    return Number(t);
  }

  async function saveOneWeight(
    field: 'weightAtLoadKg' | 'weightAtUnloadKg' | 'emptyWeightAtLoadKg' | 'emptyWeightAtUnloadKg',
    raw: string,
  ) {
    if (!openRow) return;
    const v = parseKg(raw);
    if (v !== null && (!Number.isFinite(v) || v < 0)) { setWeightMsg('Weight must be a positive number.'); return; }
    if (v !== null && (v as number) > 200_000) { setWeightMsg('That is over 200 tonnes — check the number.'); return; }

    setWeightBusy(true); setWeightMsg(null);
    try {
      await apiClient.patch(`/api/v1/dumper-trips/${openRow.id}`, { [field]: v });
      await refetch();
      setWeightMsg('Saved.');
    } catch (e) {
      setWeightMsg(extractErrorMessage(e));
    } finally {
      setWeightBusy(false);
    }
  }

  // Live difference preview from whatever is typed, before saving.
  const liveDiff = (() => {
    const l = parseKg(loadKg); const u = parseKg(unloadKg);
    if (l === null || u === null || !Number.isFinite(l) || !Number.isFinite(u)) return null;
    return (l as number) - (u as number);
  })();

  async function saveWeight() {
    if (!openRow) return;
    const raw = weightKg.trim();
    if (raw === '') { setWeightMsg('Enter a weight in kilograms.'); return; }
    const kg = Number(raw);
    if (!Number.isFinite(kg) || kg < 0) { setWeightMsg('Weight must be a positive number.'); return; }
    if (kg > 200_000) { setWeightMsg('That is over 200 tonnes — check the number.'); return; }

    setWeightBusy(true); setWeightMsg(null);
    try {
      await apiClient.patch(`/api/v1/dumper-trips/${openRow.id}`, { netWeightKg: kg });
      await refetch();
      setWeightMsg('Saved. Tons and revenue recalculated.');
    } catch (e) {
      setWeightMsg(extractErrorMessage(e));
    } finally {
      setWeightBusy(false);
    }
  }

  // Live preview of what the entered weight will produce.
  const previewTons = weightKg.trim() !== '' && Number.isFinite(Number(weightKg)) ? Number(weightKg) / 1000 : null;
  const previewRevenue = previewTons != null && openRow?.rate_per_ton_sar != null
    ? previewTons * Number(openRow.rate_per_ton_sar)
    : null;

  function chooseFile(tripId: string, slug: string) {
    setDocError(null);
    setTarget({ tripId, slug });
    fileRef.current?.click();
  }

  async function uploadDoc(file: File) {
    if (!target) return;
    const allowed = ['image/jpeg', 'image/png', 'application/pdf'];
    if (!allowed.includes(file.type)) { setDocError('Only JPG, PNG or PDF files are accepted.'); return; }
    if (file.size > 15 * 1024 * 1024) { setDocError('File is larger than 15 MB.'); return; }

    const key = `${target.tripId}:${target.slug}`;
    setBusyKey(key); setDocError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await apiClient.post(`/api/v1/dumper-trips/${target.tripId}/documents/${target.slug}`, fd);
      await refetch();
    } catch (e) {
      setDocError(extractErrorMessage(e));
    } finally {
      setBusyKey(null); setTarget(null);
    }
  }

  // The document endpoint is authenticated, so fetch it as a blob and open that
  // rather than pointing the browser at a URL it cannot authorise.
  async function viewDoc(tripId: string, slug: string) {
    try {
      const jwt = getStoredToken();
      const res = await fetch(`${API_BASE}/api/v1/dumper-trips/${tripId}/documents/${slug}/file`, {
        headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setDocError(extractErrorMessage(e));
    }
  }

  return (
    <div className="min-h-screen w-full bg-background flex flex-col">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-[1800px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="font-display text-lg">Dumpers · Loads</div>
          <div className="flex items-center gap-2">
            <Link to="/dashboard/dumpers/log"><Button size="sm" className="gap-1"><Plus className="h-4 w-4" /> Log a load</Button></Link>
            <Button variant="ghost" size="sm" onClick={() => { window.location.href = '/dashboard'; }} className="gap-2">
              <ArrowLeft className="h-4 w-4" /> Dumpers home
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-[1800px] mx-auto w-full px-6 py-8">
        {/* Bulk Excel: export the loads, edit a month at a time, upload back */}
        <div className="mb-5">
          <ExcelToolbar entity="dumper-trips" title="dumper loads" onImported={() => { void refetch(); }} />
        </div>

        {/* Search */}
        <div className="relative mb-3 max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
          <Input
            className="ps-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search code, client, truck, driver, load, amounts…"
          />
        </div>

        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="inline-flex rounded-md border border-border bg-card overflow-hidden">
            {([
              ['today', 'Today'], ['week', 'This week'], ['month', 'This month'],
              ['all', 'All time'], ['custom', 'Custom'],
            ] as [RangeKey, string][]).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setRange(key)}
                className={`px-3 py-1.5 text-sm transition-colors ${
                  range === key ? 'bg-gold/20 text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {range === 'custom' && (
            <>
              <input type="date" className={selectCls} value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} aria-label="From date" />
              <span className="text-muted-foreground text-sm">→</span>
              <input type="date" className={selectCls} value={customTo} onChange={(e) => setCustomTo(e.target.value)} aria-label="To date" />
            </>
          )}

          <select className={selectCls} value={vehicle} onChange={(e) => setVehicle(e.target.value)} aria-label="Filter by truck">
            <option value="all">All trucks</option>
            {options.vehicles.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <select className={selectCls} value={driver} onChange={(e) => setDriver(e.target.value)} aria-label="Filter by driver">
            <option value="all">All drivers</option>
            {options.drivers.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <select className={selectCls} value={client} onChange={(e) => setClient(e.target.value)} aria-label="Filter by client">
            <option value="all">All clients</option>
            {options.clients.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <select className={selectCls} value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filter by status">
            <option value="all">All statuses</option>
            {options.statuses.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>

          {filtersActive && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1">
              <X className="h-3 w-3" /> Clear
            </Button>
          )}
        </div>

        {/* Totals reflect the filtered set, not the whole table */}
        <div className="flex flex-wrap gap-6 mb-3 text-sm">
          <div><span className="text-muted-foreground">Loads:</span> <b>{rows.length}</b>{rows.length !== all.length && <span className="text-muted-foreground"> of {all.length}</span>}</div>
          <div><span className="text-muted-foreground">Total tons:</span> <b>{totals.tons.toLocaleString(undefined, { maximumFractionDigits: 2 })}</b></div>
          <div><span className="text-muted-foreground">Computed revenue:</span> <b>{totals.sar.toLocaleString(undefined, { maximumFractionDigits: 2 })} SAR</b></div>
        </div>

        {/* Selection actions */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Button variant="outline" size="sm" onClick={() => exportRows('filtered')} disabled={rows.length === 0} className="gap-1">
            <Download className="h-4 w-4" /> Export these {rows.length}
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportRows('selected')} disabled={selected.size === 0} className="gap-1">
            <Download className="h-4 w-4" /> Export selected ({selected.size})
          </Button>
          {selected.size > 0 && (
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())} className="gap-1">
              <X className="h-3 w-3" /> Clear selection
            </Button>
          )}
          {/* Appears only with a selection, beside the export that uses the same
              ticks. A load is money, so it asks why before it goes. */}
          {selected.size > 0 && canDelete && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setDelOpen(true); setDelReason(''); setDelResult(null); }}
              className="gap-1 text-rose-600 dark:text-rose-400 border-rose-500/40"
            >
              <Trash2 className="h-3.5 w-3.5" /> Delete selected ({selected.size})
            </Button>
          )}
        </div>

        {/* Legend: the Doc button opens a panel, so name what is inside it */}
        <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
          <span className="font-medium text-foreground">Doc:</span>
          <span>{DOC_SLOTS.map((s) => s.label).join(' · ')}</span>
          <span className="italic">click the paperclip on a row to open its papers and enter the weight and trip time</span>
        </div>

        {docError && (
          <p className="mb-3 text-sm text-destructive rounded border border-destructive/30 bg-destructive/10 px-3 py-2" role="alert">
            {docError}
          </p>
        )}

        {/* One hidden picker shared by every row */}
        <input
          ref={fileRef}
          type="file"
          accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
          className="sr-only"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadDoc(f); e.target.value = ''; }}
        />

        <div className="rounded-xl border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr>
                <th className="px-3 py-2 w-10">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    ref={(el) => { if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected; }}
                    onChange={toggleAllVisible}
                    aria-label="Select all rows"
                    className="cursor-pointer"
                  />
                </th>
                <th className="text-start font-medium px-3 py-2 whitespace-nowrap">Doc</th>
                <th className="text-start font-medium px-3 py-2 whitespace-nowrap">Code</th>
                <th className="text-start font-medium px-3 py-2 whitespace-nowrap">Date</th>
                <th className="text-start font-medium px-3 py-2 whitespace-nowrap" title="When the truck left for the load">Load time</th>
                <th className="text-start font-medium px-3 py-2 whitespace-nowrap" title="When the trip finished at the dump site">Unload time</th>
                <th className="text-end font-medium px-3 py-2 whitespace-nowrap" title="Unloaded at minus loaded at">Duration</th>
                <th className="text-start font-medium px-3 py-2 whitespace-nowrap">Client</th>
                <th className="text-start font-medium px-3 py-2 whitespace-nowrap">Truck</th>
                <th className="text-start font-medium px-3 py-2 whitespace-nowrap">Driver</th>
                <th className="text-start font-medium px-3 py-2 whitespace-nowrap">Load</th>
                <th className="text-end font-medium px-3 py-2 whitespace-nowrap">Tons</th>
                <th className="text-end font-medium px-3 py-2 whitespace-nowrap" title="Empty at load minus empty at unload — the same truck on two bridges, so this should be zero">Empty kg</th>
                <th className="text-end font-medium px-3 py-2 whitespace-nowrap" title="Weight at load minus weight at unload">Diff kg</th>
                <th className="text-end font-medium px-3 py-2 whitespace-nowrap">Rate</th>
                <th className="text-end font-medium px-3 py-2 whitespace-nowrap">Revenue</th>
                <th className="text-start font-medium px-3 py-2 whitespace-nowrap">Status</th>
                <th className="text-start font-medium px-3 py-2 whitespace-nowrap" title="Zoho's invoice number once the invoice is pushed there; the invoice code until then">Invoice</th>
                <th className="text-end font-medium px-3 py-2 whitespace-nowrap">Edit</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={19} className="px-4 py-6 text-center text-muted-foreground">Loading…</td></tr>}
              {!isLoading && all.length === 0 && (
                <tr><td colSpan={19} className="px-4 py-6 text-center text-muted-foreground">No loads yet. <Link className="underline" to="/dashboard/dumpers/log">Log one</Link>.</td></tr>
              )}
              {!isLoading && all.length > 0 && rows.length === 0 && (
                <tr><td colSpan={19} className="px-4 py-6 text-center text-muted-foreground">
                  No loads match these filters. <button type="button" className="underline" onClick={clearFilters}>Clear them</button>.
                </td></tr>
              )}
              {rows.map((r) => {
                const filled = DOC_SLOTS.filter((s) => !!r[s.field]).length;
                const isSelected = selected.has(r.id);
                return (
                  <tr key={r.id} className={`border-t border-border ${isSelected ? 'bg-muted/40' : ''}`}>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleRow(r.id)}
                        aria-label={`Select load ${r.code}`}
                        className="cursor-pointer"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => openPapers(r)}
                        title="Open papers and weight"
                        aria-label={`Papers for load ${r.code}: ${filled} of ${DOC_SLOTS.length} attached`}
                        className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition-colors ${
                          filled === DOC_SLOTS.length
                            ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20'
                            : 'border-border text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                        }`}
                      >
                        <Paperclip className="h-3.5 w-3.5" />
                        {filled}/{DOC_SLOTS.length}
                      </button>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{r.code}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.trip_date?.slice(0, 10)}</td>
                    {/* time on top, place underneath - one column each, so the
                        row stays readable however long a site name is */}
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="font-mono text-xs">{clock(r.trip_started_at, r.trip_date)}</div>
                      {r.trip_start_location && (
                        <div className="text-[11px] text-muted-foreground max-w-[8rem] truncate" title={r.trip_start_location}>
                          {r.trip_start_location}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="font-mono text-xs">{clock(r.trip_ended_at, r.trip_date)}</div>
                      {r.trip_end_location && (
                        <div className="text-[11px] text-muted-foreground max-w-[8rem] truncate" title={r.trip_end_location}>
                          {r.trip_end_location}
                        </div>
                      )}
                    </td>
                    <td className={`px-3 py-2 text-end whitespace-nowrap font-mono text-xs ${
                      r.trip_duration_minutes == null ? 'text-muted-foreground' : ''
                    }`}>
                      {hoursMins(r.trip_duration_minutes)}
                    </td>
                    <td className="px-3 py-2">{r.client_name ?? '—'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.vehicle_plate ?? '—'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.driver_name ?? '—'}</td>
                    <td className="px-3 py-2">{r.load_type ?? '—'}</td>
                    <td className="px-3 py-2 text-end">{num(r.net_weight_tons)}</td>
                    {/* Same colouring as Diff kg, so a positive number means
                        the same thing in both and the eye does not relearn. */}
                    <td className={`px-3 py-2 text-end font-mono ${
                      r.empty_weight_diff_kg == null ? 'text-muted-foreground'
                      : Number(r.empty_weight_diff_kg) > 0 ? 'text-amber-600'
                      : Number(r.empty_weight_diff_kg) < 0 ? 'text-rose-600'
                      : ''
                    }`}>
                      {r.empty_weight_diff_kg != null ? Number(r.empty_weight_diff_kg).toLocaleString() : '—'}
                    </td>
                    <td className={`px-3 py-2 text-end font-mono ${
                      r.weight_diff_kg == null ? 'text-muted-foreground'
                      : Number(r.weight_diff_kg) > 0 ? 'text-amber-600'
                      : Number(r.weight_diff_kg) < 0 ? 'text-rose-600'
                      : ''
                    }`}>
                      {r.weight_diff_kg != null ? Number(r.weight_diff_kg).toLocaleString() : '—'}
                    </td>
                    <td className="px-3 py-2 text-end">{num(r.rate_per_ton_sar)}</td>
                    <td className="px-3 py-2 text-end font-medium">{num(r.revenue_sar)}</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs rounded-full px-2 py-0.5 border ${
                        r.status === 'confirmed' ? 'border-emerald-500/40 text-emerald-600'
                        : r.status === 'draft' ? 'border-amber-500/40 text-amber-600'
                        : 'border-border text-muted-foreground'
                      }`}>
                        {r.status}{r.invoice_id ? ' · invoiced' : ''}
                      </span>
                    </td>
                    {/* Zoho's number when the invoice has been pushed there.
                        Nothing pushes dumper invoices to Zoho yet, so this
                        shows the invoice's own code meanwhile rather than an
                        empty column that looks broken. */}
                    <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">
                      {r.zoho_invoice_number
                        ? <span className="text-foreground">{r.zoho_invoice_number}</span>
                        : r.invoice_code
                          ? <span className="text-muted-foreground" title="Not yet in Zoho">{r.invoice_code}</span>
                          : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="px-3 py-2 text-end">
                      <button
                        type="button"
                        onClick={() => openEdit(r)}
                        title={`Edit load ${r.code}`}
                        aria-label={`Edit load ${r.code}`}
                        className="inline-flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground transition-colors hover:text-foreground hover:border-muted-foreground"
                      >
                        <Pencil className="h-3.5 w-3.5" /> Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Edit one load */}
        {editRow && draft && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            role="dialog"
            aria-modal="true"
            aria-label={`Edit load ${editRow.code}`}
            onMouseDown={(e) => { if (e.target === e.currentTarget) setEditId(null); }}
          >
            <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
              <div className="flex items-start justify-between gap-4 p-5 border-b border-border">
                <div>
                  <h2 className="font-display text-xl text-foreground">Edit load {editRow.code}</h2>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Everything about this load. Papers are still uploaded from the paperclip.
                  </p>
                </div>
                <button type="button" onClick={() => setEditId(null)} className="text-muted-foreground hover:text-foreground" aria-label="Close">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                {/* Changing an invoiced load changes what was billed. Say so
                    rather than silently allowing or silently refusing it. */}
                {editRow.invoice_id && (
                  <p className="flex items-start gap-2 text-xs rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-amber-700">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-px" />
                    This load is already on an invoice. Editing the weight, rate or client changes what was billed.
                  </p>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-muted-foreground" htmlFor="edit-date">Date</label>
                    <input
                      id="edit-date" type="date"
                      className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                      value={draft.tripDate}
                      onChange={(e) => setField('tripDate', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground" htmlFor="edit-status">Status</label>
                    <select
                      id="edit-status"
                      className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm"
                      value={draft.status}
                      onChange={(e) => setField('status', e.target.value)}
                    >
                      <option value="draft">draft</option>
                      <option value="confirmed">confirmed</option>
                      <option value="cancelled">cancelled</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-xs text-muted-foreground" htmlFor="edit-client">Client</label>
                    <select
                      id="edit-client"
                      className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm"
                      value={draft.clientId}
                      onChange={(e) => {
                        setField('clientId', e.target.value);
                        // Follow the new client's rate unless one was typed by hand.
                        const c = pickers?.clients.find((x) => x.id === e.target.value);
                        if (c?.rate) setField('ratePerTonSar', c.rate);
                      }}
                    >
                      {/* Reads as optional because it is: the client is set before invoicing,
                          not before saving a weight. */}
                      <option value="">— no client yet —</option>
                      {(pickers?.clients ?? []).map((c) => (
                        <option key={c.id} value={c.id}>{c.name}{c.rate ? ` · ${Number(c.rate).toLocaleString()} SAR/t` : ' · no rate'}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground" htmlFor="edit-truck">Truck</label>
                    <select
                      id="edit-truck"
                      className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm"
                      value={draft.vehicleId}
                      onChange={(e) => setField('vehicleId', e.target.value)}
                    >
                      <option value="">— pick a truck —</option>
                      {(pickers?.vehicles ?? []).map((v) => (
                        <option key={v.id} value={v.id}>{v.plate} · {v.code}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-xs text-muted-foreground" htmlFor="edit-driver">Driver</label>
                    <DriverSelect
                      id="edit-driver"
                      value={draft.driverId}
                      onChange={(v) => setField('driverId', v)}
                      drivers={(pickers?.drivers ?? []).map(toDriverOption)}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground" htmlFor="edit-loadtype">Load</label>
                    <Input id="edit-loadtype" value={draft.loadType} onChange={(e) => setField('loadType', e.target.value)} placeholder="sand…" />
                  </div>

                  <div>
                    <label className="text-xs text-muted-foreground" htmlFor="edit-ticket">Ticket number</label>
                    <Input id="edit-ticket" value={draft.ticketNumber} onChange={(e) => setField('ticketNumber', e.target.value)} className="font-mono" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground" htmlFor="edit-km">Km</label>
                    <Input id="edit-km" value={draft.km} onChange={(e) => setField('km', e.target.value)} inputMode="decimal" className="font-mono" />
                  </div>

                  <div>
                    <label className="text-xs text-muted-foreground" htmlFor="edit-kg">Net weight (kg)</label>
                    <Input id="edit-kg" value={draft.netWeightKg} onChange={(e) => setField('netWeightKg', e.target.value)} inputMode="decimal" className="font-mono" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground" htmlFor="edit-rate">Rate (SAR/ton)</label>
                    <Input id="edit-rate" value={draft.ratePerTonSar} onChange={(e) => setField('ratePerTonSar', e.target.value)} inputMode="decimal" className="font-mono" />
                  </div>
                </div>

                {/* The trip. These used to be reachable only from the paperclip,
                    so editing one load meant two dialogs on one record. */}
                <div className="pt-2 border-t border-border">
                  <h3 className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Loaded &rarr; unloaded</h3>
                  {/* A form that fills a column on another screen should say
                      which one, or the column looks unsettable. */}
                  <p className="text-[11px] text-muted-foreground mb-2">
                    These fill the Load time and Unload time columns.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground" htmlFor="edit-start-date">Loaded at</label>
                      <div className="flex gap-2">
                        {/* Date and time apart, because a datetime-local gives
                            nothing until both are filled - so a date typed on
                            its own used to vanish on save. */}
                        <input
                          id="edit-start-date" type="date"
                          value={splitLocal(draft.startAt).date}
                          onChange={(e) => setField('startAt', joinLocal(e.target.value, splitLocal(draft.startAt).time))}
                          className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                        />
                        <input
                          id="edit-start-time" type="time" aria-label="Loaded at, time"
                          value={splitLocal(draft.startAt).time}
                          onChange={(e) => setField('startAt', joinLocal(splitLocal(draft.startAt).date, e.target.value))}
                          className="w-28 rounded-md border border-input bg-background px-2 py-2 text-sm"
                        />
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-1">Time is optional — a date on its own is enough.</p>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground" htmlFor="edit-end-date">Unloaded at</label>
                      <div className="flex gap-2">
                        <input
                          id="edit-end-date" type="date"
                          value={splitLocal(draft.endAt).date}
                          onChange={(e) => setField('endAt', joinLocal(e.target.value, splitLocal(draft.endAt).time))}
                          className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                        />
                        <input
                          id="edit-end-time" type="time" aria-label="Unloaded at, time"
                          value={splitLocal(draft.endAt).time}
                          onChange={(e) => setField('endAt', joinLocal(splitLocal(draft.endAt).date, e.target.value))}
                          className="w-28 rounded-md border border-input bg-background px-2 py-2 text-sm"
                        />
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-1">Time is optional.</p>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground" htmlFor="edit-start-place">Loaded at (place)</label>
                      <input
                        id="edit-start-place" maxLength={200} value={draft.startPlace}
                        onChange={(e) => setField('startPlace', e.target.value)}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground" htmlFor="edit-end-place">Unloaded at (place)</label>
                      <input
                        id="edit-end-place" maxLength={200} value={draft.endPlace}
                        onChange={(e) => setField('endPlace', e.target.value)}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Both are needed before a load can be confirmed, so the dialog
                    that sets the status has to be able to supply them. */}
                <div className="pt-2 border-t border-border">
                  <h3 className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Weighbridge</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground" htmlFor="edit-w-load">Weight at load (kg)</label>
                      <input
                        id="edit-w-load" inputMode="decimal" value={draft.weightAtLoadKg}
                        onChange={(e) => setField('weightAtLoadKg', e.target.value)}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground" htmlFor="edit-w-unload">Weight at unload (kg)</label>
                      <input
                        id="edit-w-unload" inputMode="decimal" value={draft.weightAtUnloadKg}
                        onChange={(e) => setField('weightAtUnloadKg', e.target.value)}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="text-xs text-muted-foreground" htmlFor="edit-notes">Notes</label>
                  <textarea
                    id="edit-notes" rows={2} maxLength={2000}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={draft.notes}
                    onChange={(e) => setField('notes', e.target.value)}
                  />
                </div>

                {/* Tons and revenue are generated in the database; this is what
                    they will become once the change is saved. */}
                <div className="rounded-md bg-muted/40 px-3 py-2 flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    {draft.netWeightKg.trim() !== '' && Number.isFinite(Number(draft.netWeightKg))
                      ? `${(Number(draft.netWeightKg) / 1000).toFixed(3)} tons`
                      : 'no weight'}
                  </span>
                  <span className="font-mono font-medium">
                    {draftRevenue != null
                      ? `${draftRevenue.toLocaleString(undefined, { maximumFractionDigits: 2 })} SAR`
                      : 'revenue needs a weight and a rate'}
                  </span>
                </div>

                {editMsg && (
                  <p className={`text-sm ${editMsg.startsWith('Saved') ? 'text-emerald-600' : 'text-destructive'}`} role={editMsg.startsWith('Saved') ? undefined : 'alert'}>
                    {editMsg}
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-2 p-5 border-t border-border bg-muted/20">
                <Button type="button" variant="ghost" onClick={() => setEditId(null)}>Cancel</Button>
                <Button type="button" variant="gold" onClick={saveEdit} disabled={editBusy} className="gap-1">
                  {editBusy && <Loader2 className="h-4 w-4 animate-spin" />} Save changes
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Papers + weight panel for one load */}
        {openRow && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            role="dialog"
            aria-modal="true"
            aria-label={`Papers for load ${openRow.code}`}
            onMouseDown={(e) => { if (e.target === e.currentTarget) setOpenId(null); }}
          >
            <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col">
              <div className="flex items-start justify-between gap-4 p-5 border-b border-border">
                <div>
                  <h2 className="font-display text-xl text-foreground">Load {openRow.code}</h2>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {openRow.trip_date?.slice(0, 10)} · {openRow.client_name ?? 'no client'} · {openRow.vehicle_plate ?? 'no truck'}
                    {openRow.driver_name ? ` · ${openRow.driver_name}` : ''}
                  </p>
                </div>
                <button type="button" onClick={() => setOpenId(null)} className="text-muted-foreground hover:text-foreground" aria-label="Close">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-5 space-y-5">
                {/* Trip start -> end */}
                <section className="space-y-2">
                  <h3 className="text-xs uppercase tracking-widest text-muted-foreground">Loaded → unloaded</h3>

                  {/* Past places, filtered by the browser as you type. Typing
                      something new is always allowed. */}
                  <datalist id="panel-places">
                    {(places ?? []).map((p) => <option key={p.name} value={p.name} />)}
                  </datalist>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <div>
                        <label className="text-xs text-muted-foreground" htmlFor="panel-start">Loaded at</label>
                        <input
                          id="panel-start"
                          type="datetime-local"
                          className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                          value={startAt}
                          onChange={(e) => { setStartAt(e.target.value); setTimeMsg(null); }}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground" htmlFor="panel-start-place">Loaded at (place)</label>
                        <input
                          id="panel-start-place"
                          list="panel-places"
                          className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                          value={startPlace}
                          maxLength={200}
                          onChange={(e) => { setStartPlace(e.target.value); setTimeMsg(null); }}
                          placeholder="where it loaded…"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div>
                        <label className="text-xs text-muted-foreground" htmlFor="panel-end">Unloaded at</label>
                        <input
                          id="panel-end"
                          type="datetime-local"
                          className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                          value={endAt}
                          min={startAt || undefined}
                          onChange={(e) => { setEndAt(e.target.value); setTimeMsg(null); }}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground" htmlFor="panel-end-place">Unloaded at (place)</label>
                        <input
                          id="panel-end-place"
                          list="panel-places"
                          className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                          value={endPlace}
                          maxLength={200}
                          onChange={(e) => { setEndPlace(e.target.value); setTimeMsg(null); }}
                          placeholder="where it dumped…"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="underline text-muted-foreground hover:text-foreground"
                        onClick={() => { setStartAt(nowLocalInput()); setTimeMsg(null); }}
                      >
                        Start now
                      </button>
                      <button
                        type="button"
                        className="underline text-muted-foreground hover:text-foreground"
                        onClick={() => { setEndAt(nowLocalInput()); setTimeMsg(null); }}
                      >
                        End now
                      </button>
                      <button
                        type="button"
                        className="underline text-muted-foreground hover:text-foreground"
                        onClick={() => { setStartAt(''); setEndAt(''); setTimeMsg(null); }}
                      >
                        Clear times
                      </button>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={liveDuration != null && liveDuration < 0 ? 'text-destructive' : 'text-muted-foreground'}>
                        Duration:{' '}
                        <b className="text-foreground">
                          {liveDuration != null
                            ? (liveDuration < 0 ? 'end is before start' : hoursMins(liveDuration))
                            : hoursMins(openRow.trip_duration_minutes)}
                        </b>
                      </span>
                      <Button type="button" variant="outline" size="sm" onClick={saveTimes} disabled={timeBusy} className="gap-1">
                        {timeBusy && <Loader2 className="h-4 w-4 animate-spin" />} Save
                      </Button>
                    </div>
                  </div>
                  {timeMsg && (
                    <p className={`text-xs ${timeMsg.startsWith('Saved') ? 'text-emerald-600' : 'text-destructive'}`}>{timeMsg}</p>
                  )}
                </section>

                {/* Manual weight */}
                <section className="space-y-2">
                  <h3 className="text-xs uppercase tracking-widest text-muted-foreground">Weight</h3>
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <label className="text-xs text-muted-foreground" htmlFor="weight-kg">Net weight (kg)</label>
                      <Input
                        id="weight-kg"
                        value={weightKg}
                        onChange={(e) => { setWeightKg(e.target.value); setWeightMsg(null); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void saveWeight(); } }}
                        inputMode="decimal"
                        placeholder="off the scale ticket, e.g. 28400"
                        className="font-mono"
                      />
                    </div>
                    <Button type="button" variant="gold" onClick={saveWeight} disabled={weightBusy} className="gap-1">
                      {weightBusy && <Loader2 className="h-4 w-4 animate-spin" />} Save
                    </Button>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {previewTons != null ? (
                      <>
                        {previewTons.toFixed(3)} tons
                        {openRow.rate_per_ton_sar != null && previewRevenue != null
                          ? <> × {Number(openRow.rate_per_ton_sar).toLocaleString()} SAR/t = <b className="text-foreground">{previewRevenue.toLocaleString(undefined, { maximumFractionDigits: 2 })} SAR</b></>
                          : ' · no rate on this load, revenue cannot be computed'}
                      </>
                    ) : (
                      <>Currently {num(openRow.net_weight_tons)} tons · {num(openRow.revenue_sar)} SAR</>
                    )}
                  </div>
                  {weightMsg && (
                    <p className={`text-xs ${weightMsg.startsWith('Saved') ? 'text-emerald-600' : 'text-destructive'}`}>{weightMsg}</p>
                  )}
                </section>

                {/* Papers */}
                <section className="space-y-2">
                  <h3 className="text-xs uppercase tracking-widest text-muted-foreground">
                    Papers · {DOC_SLOTS.filter((s) => !!openRow[s.field]).length}/{DOC_SLOTS.length}
                  </h3>
                  <ul className="rounded-md border border-border divide-y divide-border">
                    {DOC_SLOTS.map((s) => {
                      const path = openRow[s.field];
                      const has = !!path;
                      const isPdf = typeof path === 'string' && path.endsWith('.pdf');
                      const busy = busyKey === `${openRow.id}:${s.slug}`;
                      // The two weighbridge readings sit directly under the
                      // paper they are taken with.
                      const weighAt = s.slug === 'hopper' ? 'load' : s.slug === 'unload' ? 'unload' : null;
                      return (
                        <li key={s.slug}>
                          <div className="flex items-center justify-between gap-3 px-3 py-2.5">
                            <div className="min-w-0">
                              <div className="text-sm font-medium flex items-center gap-2">
                                {s.label}
                                {has && <span className="text-[10px] rounded-full border border-emerald-500/40 text-emerald-600 px-1.5">{isPdf ? 'PDF' : 'photo'}</span>}
                              </div>
                              <div className="text-[11px] text-muted-foreground">{has ? s.hint : `${s.hint} · not uploaded`}</div>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                              {busy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                              {has && (
                                <Button type="button" variant="outline" size="sm" onClick={() => viewDoc(openRow.id, s.slug)}>
                                  View
                                </Button>
                              )}
                              <Button type="button" variant={has ? 'ghost' : 'outline'} size="sm" onClick={() => chooseFile(openRow.id, s.slug)} disabled={busy}>
                                {has ? 'Replace' : 'Upload'}
                              </Button>
                            </div>
                          </div>

                          {weighAt && (
                            <div className="px-3 pb-2.5 -mt-1 flex items-end gap-2">
                              <div className="flex-1">
                                <label className="text-[11px] text-muted-foreground" htmlFor={`w-${weighAt}`}>
                                  Weight at {weighAt} (kg)
                                </label>
                                <Input
                                  id={`w-${weighAt}`}
                                  value={weighAt === 'load' ? loadKg : unloadKg}
                                  onChange={(e) => {
                                    setWeightMsg(null);
                                    if (weighAt === 'load') setLoadKg(e.target.value); else setUnloadKg(e.target.value);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      void saveOneWeight(weighAt === 'load' ? 'weightAtLoadKg' : 'weightAtUnloadKg',
                                        weighAt === 'load' ? loadKg : unloadKg);
                                    }
                                  }}
                                  inputMode="decimal"
                                  placeholder="weighbridge reading"
                                  className="font-mono h-9"
                                />
                              </div>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={weightBusy}
                                onClick={() => saveOneWeight(
                                  weighAt === 'load' ? 'weightAtLoadKg' : 'weightAtUnloadKg',
                                  weighAt === 'load' ? loadKg : unloadKg,
                                )}
                              >
                                Save
                              </Button>
                            </div>
                          )}

                          {/* The empty reading from the same bridge visit. Kept
                              with its paper because that is where it is read
                              from - putting it elsewhere would mean opening two
                              screens to record one weighing. */}
                          {weighAt && (
                            <div className="px-3 pb-2.5 flex items-end gap-2">
                              <div className="flex-1">
                                <label className="text-[11px] text-muted-foreground" htmlFor={`ew-${weighAt}`}>
                                  Empty weight at {weighAt} (kg)
                                </label>
                                <Input
                                  id={`ew-${weighAt}`}
                                  value={weighAt === 'load' ? emptyLoadKg : emptyUnloadKg}
                                  onChange={(e) => {
                                    setWeightMsg(null);
                                    if (weighAt === 'load') setEmptyLoadKg(e.target.value);
                                    else setEmptyUnloadKg(e.target.value);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      void saveOneWeight(
                                        weighAt === 'load' ? 'emptyWeightAtLoadKg' : 'emptyWeightAtUnloadKg',
                                        weighAt === 'load' ? emptyLoadKg : emptyUnloadKg,
                                      );
                                    }
                                  }}
                                  inputMode="decimal"
                                  placeholder="tare reading"
                                  className="font-mono h-9"
                                />
                              </div>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={weightBusy}
                                onClick={() => saveOneWeight(
                                  weighAt === 'load' ? 'emptyWeightAtLoadKg' : 'emptyWeightAtUnloadKg',
                                  weighAt === 'load' ? emptyLoadKg : emptyUnloadKg,
                                )}
                              >
                                Save
                              </Button>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                  <p className="text-[11px] text-muted-foreground">JPG, PNG or PDF · up to 15 MB each</p>

                  {/* load - unload, shown live while typing and after saving */}
                  <div className="rounded-md bg-muted/40 px-3 py-2 flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Difference (load − unload)</span>
                    <span className={`font-mono font-medium ${
                      (liveDiff ?? Number(openRow.weight_diff_kg)) > 0 ? 'text-amber-600'
                      : (liveDiff ?? Number(openRow.weight_diff_kg)) < 0 ? 'text-rose-600'
                      : ''
                    }`}>
                      {liveDiff != null
                        ? `${liveDiff.toLocaleString()} kg`
                        : openRow.weight_diff_kg != null
                          ? `${Number(openRow.weight_diff_kg).toLocaleString()} kg`
                          : '—'}
                    </span>
                  </div>
                </section>
              </div>

              <div className="flex justify-end p-5 border-t border-border bg-muted/20">
                <Button type="button" variant="outline" onClick={() => setOpenId(null)}>Done</Button>
              </div>
            </div>
          </div>
        )}
        {delOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
               onClick={() => { if (!delBusy) setDelOpen(false); }}>
            <div className="bg-card border border-border rounded-lg p-5 max-w-md w-full"
                 onClick={(e) => e.stopPropagation()}>
              <h3 className="font-display text-lg mb-1">Delete {selected.size} load{selected.size === 1 ? '' : 's'}?</h3>
              <p className="text-xs text-muted-foreground mb-3">
                Loads that are on an invoice will be left alone and named back to you —
                take them off the invoice first on the Receivables page.
              </p>
              <textarea
                value={delReason}
                onChange={(e) => setDelReason(e.target.value)}
                rows={3}
                autoFocus
                placeholder="Why are these being deleted?"
                className="w-full rounded-md border border-input bg-background p-2 text-sm"
              />
              {delResult && (
                <div className="mt-3 rounded-md border border-border bg-muted/40 p-2 text-sm">{delResult}</div>
              )}
              <div className="flex gap-2 mt-4">
                <Button
                  variant="outline"
                  className="text-rose-600 dark:text-rose-400 border-rose-500/40"
                  disabled={delBusy || delReason.trim().length < 3}
                  onClick={() => void doDelete()}
                >
                  {delBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  Delete
                </Button>
                <Button variant="ghost" onClick={() => setDelOpen(false)} disabled={delBusy}>Close</Button>
              </div>
            </div>
          </div>
        )}

      </main>
    </div>
  );
}
